import React, { useEffect, useMemo, useState } from 'react';
import type {
  ChangeCabDeckElement,
  ChangeCabDeckImageCrop,
  ChangeCabDeckSlide,
  ChangeCabDeckTheme,
  ChangeMeetingItem,
} from '../../../types/changes';
import { fetchAuthenticatedObjectUrl } from '../../../utils/fileDownload';
import { cabDeckThemeToCssVars, type CabDeckTheme } from '../cabDeckTheme';
import { normalizeCabDeckRichHtml } from '../cabDeckRichText';
import {
  isBrandCabTemplate,
  resolveSlideLayout,
  rewriteLegacyCabBrandImagePath,
  type CabBrandLayout,
  type CabConclusaoLayout,
  type CabRiscosLayout,
} from '../cabPresentationTemplates';
import '../cabPresentationTheme.css';
import PetronectLogo, { PetronectMark } from './PetronectLogo';

/** URL de imagem do deck: http(s), path /… ou data:image/*;base64 do upload local. */
export function safeCabDeckImageSrc(src: string | null | undefined): string | null {
  let value = String(src || '').trim();
  if (!value) return null;
  // Artes CCO-531: /cab-cco legado → seed autenticado.
  value = rewriteLegacyCabBrandImagePath(value);
  if (/^javascript:/i.test(value) || value.startsWith('//')) return null;
  if (/^https?:\/\//i.test(value)) {
    return value.length <= 2048 ? value : null;
  }
  if (value.startsWith('/') && !value.startsWith('//')) {
    return value.length <= 2048 ? value : null;
  }
  if (/^data:image\/(png|jpeg|jpg|gif|webp);base64,[A-Za-z0-9+/=]+$/i.test(value)) {
    return value.length <= 7_000_000 ? value : null;
  }
  return null;
}

function needsAuthFetch(src: string): boolean {
  return src.startsWith('/uploads/') || src.startsWith('uploads/');
}

const CabDeckRichHtml = React.memo(function CabDeckRichHtml({ raw }: { raw: string }) {
  const html = useMemo(() => normalizeCabDeckRichHtml(raw), [raw]);
  if (!html) return null;
  return (
    <div className="cab-deck-el-text cab-deck-el-rich" dangerouslySetInnerHTML={{ __html: html }} />
  );
});

function asCabTheme(theme: ChangeCabDeckTheme | CabDeckTheme): CabDeckTheme {
  return {
    bg: theme.bg,
    blue: theme.blue,
    green: theme.green,
    text: theme.text,
    chromeBg: theme.chromeBg,
    chromeText: theme.chromeText,
    chromeAccent: theme.chromeAccent,
    titleFontFamily: theme.titleFontFamily,
    titleFontSizeVw: theme.titleFontSizeVw,
    titleFontWeight: theme.titleFontWeight,
    dateFontSizeRem: theme.dateFontSizeRem,
    capaFixedTitle: 'capaFixedTitle' in theme ? theme.capaFixedTitle : '',
    useChangeTitleOnCapa: 'useChangeTitleOnCapa' in theme ? theme.useChangeTitleOnCapa : true,
  };
}

export function elementKey(el: ChangeCabDeckElement, index: number): string | number {
  return el.clientKey || el.id || `tmp-${index}`;
}

/** Converte “% da largura do slide” (campo fontSizeVw) em px — igual na edição e na apresentação. */
export function slideFontPx(fontSizeVw: number, slideWidthPx: number, scale = 1): string {
  const safeW = Math.max(0, slideWidthPx);
  const safeVw = Number.isFinite(fontSizeVw) ? fontSizeVw : 1.05;
  const safeScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
  return `${(safeVw / 100) * safeW * safeScale}px`;
}

function resolveElementText(
  el: ChangeCabDeckElement,
  fields?: Record<string, string> | null,
): string {
  if (el.type === 'field') {
    const key = el.field_key || '';
    return (fields && fields[key]) || '—';
  }
  return el.content || '';
}

function normalizeCrop(crop?: ChangeCabDeckImageCrop | null): ChangeCabDeckImageCrop {
  const xPct = Math.max(0, Math.min(95, crop?.xPct ?? 0));
  const yPct = Math.max(0, Math.min(95, crop?.yPct ?? 0));
  const wPct = Math.max(5, Math.min(100 - xPct, crop?.wPct ?? 100));
  const hPct = Math.max(5, Math.min(100 - yPct, crop?.hPct ?? 100));
  return { xPct, yPct, wPct, hPct };
}

function CabRiscosTable({
  layout,
  heading,
}: {
  layout: CabRiscosLayout;
  heading?: string;
}) {
  const rows = layout.rows || [];
  const title = (heading || 'Riscos e Impactos').toUpperCase();
  return (
    <div className="cab-cco-tpl-riscos">
      <div className="cab-cco-section-head cab-cco-tpl-heading">{title}</div>
      <table className="cab-cco-riscos-table">
        <thead>
          <tr>
            <th scope="col">RISCOS</th>
            <th scope="col">STATUS</th>
            <th scope="col">COMENTÁRIOS</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`risco-${index}`}>
              <td>{row.risco || '\u00a0'}</td>
              <td>{row.status || '\u00a0'}</td>
              <td>{row.comentarios || '\u00a0'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CabConclusaoMatrix({
  layout,
  interactive = false,
  onStarPlace,
}: {
  layout: CabConclusaoLayout;
  interactive?: boolean;
  onStarPlace?: (phaseIndex: number, dateIndex: number) => void;
}) {
  const phases = layout.phases || [];
  const dates = layout.dates || [];
  const starP = layout.star_phase_index ?? -1;
  const starD = layout.star_date_index ?? -1;
  return (
    <div className="cab-cco-tpl-conclusao">
      <div className="cab-cco-conclusao-scroll">
        <table className="cab-cco-conclusao-table">
          <thead>
            <tr>
              <th scope="col" className="cab-cco-conclusao-corner">
                Fase
              </th>
              {dates.map((date, di) => (
                <th key={`d-${di}`} scope="col" title={date}>
                  {date}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {phases.map((phase, pi) => (
              <tr key={`p-${pi}`}>
                <th scope="row" title={phase}>
                  {phase}
                </th>
                {dates.map((_, di) => {
                  const on = Boolean(layout.active?.[pi]?.[di]);
                  const star = pi === starP && di === starD;
                  return (
                    <td
                      key={`c-${pi}-${di}`}
                      className={[
                        on ? 'is-active' : '',
                        star ? 'is-star' : '',
                        interactive && onStarPlace ? 'is-pickable' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={
                        interactive && onStarPlace
                          ? () => onStarPlace(pi, di)
                          : undefined
                      }
                      onKeyDown={
                        interactive && onStarPlace
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onStarPlace(pi, di);
                              }
                            }
                          : undefined
                      }
                      role={interactive && onStarPlace ? 'button' : undefined}
                      tabIndex={interactive && onStarPlace ? 0 : undefined}
                      title={
                        interactive && onStarPlace
                          ? 'Clique para marcar esta data com a estrela'
                          : undefined
                      }
                    >
                      {star ? <span className="cab-cco-star" aria-label="Marco">★</span> : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CabBrandBleed({
  layout,
  fallbackSrc,
}: {
  layout: CabBrandLayout;
  fallbackSrc?: string | null;
}) {
  const src = safeCabDeckImageSrc(layout.backgroundImage || fallbackSrc || '');
  if (!src) {
    return <div className="cab-deck-img-empty">Sem imagem de marca</div>;
  }
  /* Arte estática em /cab-cco (public/): <img> direto. Upload /uploads/ usa ImageElement. */
  if (needsAuthFetch(src)) {
    return (
      <div className="cab-cco-tpl-brand">
        <ImageElement src={src} objectFit="cover" className="cab-cco-tpl-brand-img" />
      </div>
    );
  }
  return (
    <div className="cab-cco-tpl-brand">
      <img
        className="cab-cco-tpl-brand-img"
        src={src}
        alt=""
        decoding="async"
        fetchPriority="high"
        draggable={false}
      />
    </div>
  );
}

function ImageElement({
  src,
  objectFit,
  crop,
  className,
}: {
  src: string;
  objectFit: 'contain' | 'cover' | 'fill';
  crop?: ChangeCabDeckImageCrop | null;
  className?: string;
}) {
  const [displaySrc, setDisplaySrc] = useState<string | null>(() =>
    needsAuthFetch(src) ? null : src,
  );
  const [loading, setLoading] = useState(() => needsAuthFetch(src));

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    if (!needsAuthFetch(src)) {
      setLoading(false);
      setDisplaySrc(src);
      return undefined;
    }

    setLoading(true);
    setDisplaySrc(null);
    void Promise.resolve(fetchAuthenticatedObjectUrl(src))
      .then((url) => {
        if (cancelled) {
          if (typeof window.URL.revokeObjectURL === 'function') {
            window.URL.revokeObjectURL(url);
          }
          return;
        }
        objectUrl = url;
        setDisplaySrc(url);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setDisplaySrc(null);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl && typeof window.URL.revokeObjectURL === 'function') {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]);

  const region = normalizeCrop(crop);
  const fullFrame = region.xPct === 0 && region.yPct === 0 && region.wPct === 100 && region.hPct === 100;

  if (loading) {
    return <div className="cab-deck-img-empty">Carregando imagem…</div>;
  }
  if (!displaySrc) {
    return <div className="cab-deck-img-empty">Sem imagem</div>;
  }

  if (fullFrame) {
    return (
      <img
        className={className}
        src={displaySrc}
        alt=""
        decoding="async"
        loading="eager"
        style={{ width: '100%', height: '100%', objectFit }}
        draggable={false}
      />
    );
  }
  const scaleX = 100 / region.wPct;
  const scaleY = 100 / region.hPct;
  return (
    <div className="cab-deck-img-crop">
      <img
        className={className}
        src={displaySrc}
        alt=""
        decoding="async"
        loading="eager"
        draggable={false}
        style={{
          position: 'absolute',
          left: `${(-region.xPct / region.wPct) * 100}%`,
          top: `${(-region.yPct / region.hPct) * 100}%`,
          width: `${scaleX * 100}%`,
          height: `${scaleY * 100}%`,
          objectFit: 'fill',
          maxWidth: 'none',
        }}
      />
    </div>
  );
}

export function CabDeckSlideCanvas({
  slide,
  theme,
  fields,
  showRail = true,
  selectedId,
  onSelect,
  interactive = false,
  fontScale = 1,
  onStarPlace,
}: {
  slide: ChangeCabDeckSlide | null;
  theme: ChangeCabDeckTheme | CabDeckTheme;
  fields?: Record<string, string> | null;
  showRail?: boolean;
  selectedId?: number | string | null;
  onSelect?: (id: number | string) => void;
  interactive?: boolean;
  fontScale?: number;
  /** Editor: clicar na célula da matriz Conclusão move a estrela. */
  onStarPlace?: (phaseIndex: number, dateIndex: number) => void;
}) {
  const cabTheme = asCabTheme(theme);
  const scale = Number.isFinite(fontScale) && fontScale > 0 ? fontScale : 1;
  const stageRef = React.useRef<HTMLDivElement | null>(null);
  const [slideW, setSlideW] = useState(0);
  const measureRafRef = React.useRef<number | null>(null);

  useEffect(() => {
    const node = stageRef.current;
    if (!node) return undefined;

    const applyWidth = (raw: number) => {
      /* Inteiro + histerese: evita loop ResizeObserver ↔ fonte em px ↔ scrollbar. */
      const w = Math.round(raw);
      if (w <= 0) return;
      setSlideW((prev) => (Math.abs(prev - w) < 2 ? prev : w));
    };

    const measure = () => {
      if (measureRafRef.current != null) return;
      measureRafRef.current = window.requestAnimationFrame(() => {
        measureRafRef.current = null;
        applyWidth(node.getBoundingClientRect().width);
      });
    };

    applyWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => {
      ro.disconnect();
      if (measureRafRef.current != null) {
        window.cancelAnimationFrame(measureRafRef.current);
        measureRafRef.current = null;
      }
    };
  }, [slide?.id, interactive]);

  const themeStyle = {
    ...cabDeckThemeToCssVars(cabTheme),
    ['--cab-font-scale' as string]: String(scale),
    ...(slideW > 0
      ? {
          ['--cab-slide-w' as string]: `${slideW}px`,
          ['--cab-cco-title-size' as string]: slideFontPx(cabTheme.titleFontSizeVw, slideW, 1),
        }
      : {}),
  };
  const { template, layout: resolvedLayout } = useMemo(
    () => resolveSlideLayout(slide?.title, slide?.layout),
    [slide?.title, slide?.layout],
  );
  const isCapa = template === 'capa';
  const isBrand = isBrandCabTemplate(template);
  const isRiscos = template === 'riscos_impactos';
  const isConclusao = template === 'conclusao';
  const useStructured = isRiscos || isConclusao || isBrand;
  const showSideRail = showRail && !isCapa && !isBrand;
  const elements = useMemo(
    () =>
      [...(slide?.elements || [])].sort(
        (a, b) => a.sort_order - b.sort_order || (a.id || 0) - (b.id || 0),
      ),
    [slide?.elements],
  );
  const brandFallback = elements.find((el) => el.type === 'image')?.content || null;
  const railSubtitle =
    (isRiscos || isConclusao) &&
    resolvedLayout &&
    typeof (resolvedLayout as { subtitle?: unknown }).subtitle === 'string'
      ? String((resolvedLayout as { subtitle?: string }).subtitle || '').trim()
      : '';

  const brandBgSrc =
    isBrand && resolvedLayout && 'backgroundImage' in resolvedLayout
      ? safeCabDeckImageSrc((resolvedLayout as CabBrandLayout).backgroundImage || brandFallback)
      : null;
  const overlayElements = useMemo(() => {
    if (!isBrand || !brandBgSrc) return elements;
    return elements.filter((el) => {
      if (el.type !== 'image') return true;
      const src = safeCabDeckImageSrc(el.content);
      /* Evita duplicar o full-bleed do template brand (seed) — overlays extras continuam. */
      if (src && brandBgSrc && src === brandBgSrc) return false;
      return true;
    });
  }, [elements, isBrand, brandBgSrc]);

  const renderFreeformElements = (layerClass: string) => (
    <div className={layerClass}>
      {overlayElements.map((el, index) => {
        const key = elementKey(el, index);
        const selected = selectedId != null && String(selectedId) === String(key);
        const style = el.style || {};
        const objectFit = style.objectFit || 'contain';
        const baseVw = style.fontSizeVw || (el.type === 'label' ? 1.2 : 1.05);
        const fontSize = slideW > 0 ? slideFontPx(baseVw, slideW, scale) : `${baseVw * scale}vw`;
        const isRich = el.type === 'label' || el.type === 'text';
        const textColor = isRich
          ? el.type === 'label'
            ? 'var(--cab-cco-blue)'
            : 'var(--cab-cco-black)'
          : style.color || (el.type === 'label' ? 'var(--cab-cco-blue)' : 'var(--cab-cco-black)');
        const imageSrc = el.type === 'image' ? safeCabDeckImageSrc(el.content) : null;
        return (
          <div
            key={key}
            className={`cab-deck-el${selected ? ' is-selected' : ''}${interactive ? ' is-interactive' : ''}`}
            style={{
              left: `${el.x_pct}%`,
              top: `${el.y_pct}%`,
              width: `${el.w_pct}%`,
              height: `${el.h_pct}%`,
              zIndex: Math.max(1, Math.round(el.sort_order) + 1),
              color: textColor,
              fontWeight: style.fontWeight || (el.type === 'label' ? 700 : 400),
              fontSize,
              textAlign: style.textAlign || 'left',
            }}
            onMouseDown={interactive && onSelect ? () => onSelect(key) : undefined}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
          >
            {el.type === 'image' ? (
              imageSrc ? (
                <ImageElement src={imageSrc} objectFit={objectFit} crop={style.crop} />
              ) : (
                <div className="cab-deck-img-empty">
                  Sem imagem — selecione e envie um arquivo à direita
                </div>
              )
            ) : el.type === 'field' ? (
              <div className="cab-deck-el-text">{resolveElementText(el, fields)}</div>
            ) : (
              <CabDeckRichHtml raw={resolveElementText(el, fields)} />
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      ref={stageRef}
      className={`cab-cco-stage cab-deck-canvas${isBrand ? ' cab-deck-canvas--brand' : ''}`}
      data-template={template}
      data-font-scale={scale}
      style={themeStyle}
    >
      {showSideRail ? (
        <aside className="cab-cco-rail" aria-hidden="true">
          <div className="cab-cco-rail-body">
            <div className="cab-cco-rail-accent" />
            <div className="cab-cco-rail-title">{(slide?.title || 'MUDANÇA').toUpperCase()}</div>
            {railSubtitle ? <div className="cab-cco-rail-sub">{railSubtitle}</div> : null}
          </div>
          <div className="cab-cco-rail-logo">
            <PetronectMark variant="white" />
          </div>
        </aside>
      ) : null}
      {isCapa ? (
        <div className="cab-cco-capa-brand">
          <PetronectLogo />
        </div>
      ) : null}
      {useStructured ? (
        <div className="cab-deck-canvas-layer cab-deck-canvas-layer--tpl">
          {isRiscos && resolvedLayout && 'rows' in resolvedLayout ? (
            <CabRiscosTable
              layout={resolvedLayout as CabRiscosLayout}
              heading={slide?.title || 'Riscos e Impactos'}
            />
          ) : null}
          {isConclusao && resolvedLayout && 'phases' in resolvedLayout ? (
            <CabConclusaoMatrix
              layout={resolvedLayout as CabConclusaoLayout}
              interactive={interactive}
              onStarPlace={onStarPlace}
            />
          ) : null}
          {isBrand && resolvedLayout ? (
            <CabBrandBleed
              layout={resolvedLayout as CabBrandLayout}
              fallbackSrc={brandFallback}
            />
          ) : null}
        </div>
      ) : null}
      {renderFreeformElements(
        `cab-deck-canvas-layer${isCapa ? ' cab-deck-canvas-layer--capa' : ''}${
          useStructured ? ' cab-deck-canvas-layer--overlay' : ''
        }`,
      )}
      {isCapa ? (
        <div className="cab-cco-capa-mark">
          <PetronectMark variant="green" />
        </div>
      ) : null}
    </div>
  );
}

export function meetingItemFields(item?: ChangeMeetingItem | null): Record<string, string> {
  return (item?.fields || {}) as Record<string, string>;
}

export default CabDeckSlideCanvas;
